/**
 * WarrantyClaimHandler - Manages warranty claim form submission and validation
 * Handles file uploads, form validation, and API communication
 */
class WarrantyClaimHandler {
  // Constants
  static CONSTANTS = {
    SELECTORS: {
      FORM: '.warranty-form',
      CONTAINER: '.warranty-form',
      SUBMIT_BUTTON: 'button[type="submit"]',
      FILE_INPUT: '#proof',
      FILE_BUTTON: '.proof-file-btn',
      DROPZONE: '.proof-dropzone',
      FILE_LIST: '#fileList',
      REMOVE_FILE_BTN: '.remove-file-btn'
    },
    API: {
      UPLOAD_URL: 'https://formbuilder.hulkapps.com/forms/file_upload/2_hkLenGtncJKXg2C-GQ-A',
      SUBMIT_URL: 'https://formbuilder.hulkapps.com/forms/create_response/2_hkLenGtncJKXg2C-GQ-A',
      ACCESS_TOKEN: '6003ad618bdb6a61',
      BATCH_VALIDATION_URL: '/apps/zip-code-validator/api/parts-details',
      ORDER_VALIDATION_URL: '/apps/zip-code-validator/api/order-details'
    },
    VALIDATION: {
      BATCH_CODE_PATTERN: /^[A-Za-z0-9]{4,20}$/,
      WARRANTY_PERIOD_MONTHS: 3,
      FILE_SIZE_UNITS: ['Bytes', 'KB', 'MB', 'GB'],
      BYTES_PER_UNIT: 1024
    },
    MESSAGES: {
      SUCCESS: {
        FORM_SUBMITTED: 'Form submitted successfully!',
        BATCH_CODE_VALID: 'Batch code validation successful'
      },
      ERRORS: {
        FORM_NOT_FOUND: 'Form element not found',
        CONTAINER_NOT_FOUND: 'Container element not found',
        SUBMIT_BUTTON_NOT_FOUND: 'Submit button not found',
        BATCH_CODE_EMPTY: 'Batch code cannot be empty',
        BATCH_CODE_INVALID: 'Invalid batch code format',
        BATCH_CODE_NOT_FOUND: 'Invalid batch code or no parts found',
        BATCH_CODE_API_ERROR: 'Error validating batch code. Please try again.',
        DATE_INCOMPLETE: 'Please enter a complete date or leave all date fields empty.',
        MONTH_INVALID: 'Please enter a valid month (01-12).',
        DAY_INVALID: 'Please enter a valid day (01-31).',
        YEAR_INVALID: 'Please enter a valid year.'
      }
    },
    STYLES: {
      SUCCESS_COLOR: '#28a745',
      SUCCESS_BG: '#e8f5e8',
      DEFAULT_COLOR: '#656263',
      DEFAULT_BG: '#F1F1F1',
      BORDER_COLOR: '#A7A5A6'
    }
  };

  /**
   * Initialize the WarrantyClaimHandler
   * @param {Object} options - Configuration options
   * @param {string} options.formSelector - CSS selector for the form element
   * @param {string} options.containerSelector - CSS selector for the container element
   * @param {string} options.submitButtonSelector - CSS selector for the submit button
   * @param {string} options.fileInputSelector - CSS selector for the file input
   * @param {string} options.uploadUrl - API URL for file uploads
   * @param {string} options.submitUrl - API URL for form submission
   * @param {string} options.accessToken - API access token
   */
  constructor(options = {}) {
    // Selectors configuration
    this.selectors = {
      form: options.formSelector || WarrantyClaimHandler.CONSTANTS.SELECTORS.FORM,
      container: options.containerSelector || WarrantyClaimHandler.CONSTANTS.SELECTORS.CONTAINER,
      submitButton: options.submitButtonSelector || WarrantyClaimHandler.CONSTANTS.SELECTORS.SUBMIT_BUTTON,
      fileInput: options.fileInputSelector || WarrantyClaimHandler.CONSTANTS.SELECTORS.FILE_INPUT
    };
    
    // API configuration
    this.apiConfig = {
      uploadUrl: options.uploadUrl || WarrantyClaimHandler.CONSTANTS.API.UPLOAD_URL,
      submitUrl: options.submitUrl || WarrantyClaimHandler.CONSTANTS.API.SUBMIT_URL,
      accessToken: options.accessToken || WarrantyClaimHandler.CONSTANTS.API.ACCESS_TOKEN
    };
    
    // DOM elements cache
    this.elements = {
      form: null,
      container: null,
      submitButton: null,
      fileInput: null
    };
    
    // State management
    this.state = {
      isSubmitting: false,
      isValidating: false,
      selectedFiles: [],
      lastSubmitTime: 0
    };
    
    // Add unique instance ID for internal tracking
    this.instanceId = Math.random().toString(36).substr(2, 9);
    
    this.init();
  }

  /**
   * Initialize the warranty claim handler
   * Sets up DOM elements and event listeners when DOM is ready
   */
  init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.onDOMReady());
    } else {
      this.onDOMReady();
    }
  }

  /**
   * Handle DOM ready event
   * @private
   */
  onDOMReady() {
    this.cacheElements();
    this.attachEventListeners();
  }

  /**
   * Cache DOM elements for better performance
   * @returns {boolean} True if all required elements are found
   * @private
   */
  cacheElements() {
    const { form, container, submitButton, fileInput } = this.selectors;
    
    this.elements.form = document.querySelector(form);
    this.elements.container = document.querySelector(container);
    this.elements.submitButton = document.querySelector(submitButton);
    this.elements.fileInput = document.querySelector(fileInput);
    
    const missingElements = [];
    
    if (!this.elements.form) {
      missingElements.push(`Form element (${form})`);
    }
    
    if (!this.elements.container) {
      missingElements.push(`Container element (${container})`);
    }
    
    if (missingElements.length > 0) {
      return false;
    }
    
    return true;
  }

  /**
   * Attach event listeners to form elements
   * @private
   */
  attachEventListeners() {
    const { submitButton, fileInput } = this.elements;
    
    if (submitButton) {
      submitButton.addEventListener('click', this.handleSubmitClick.bind(this));
    }
    
    if (fileInput) {
      fileInput.addEventListener('change', this.handleFileChange.bind(this));
    }
  }

  /**
   * Handle submit button click
   * @param {Event} event - Click event
   * @private
   */
  handleSubmitClick(event) {
    event.preventDefault();
    
    const currentTime = Date.now();
    const timeSinceLastSubmit = currentTime - this.state.lastSubmitTime;
    
    // Prevent rapid double-clicks with both state check and time debounce (500ms)
    if (this.state.isSubmitting) {
      return;
    }
    
    if (timeSinceLastSubmit < 500) {
      return;
    }
    
    // Set submitting state and timestamp immediately to prevent double-clicks
    this.state.isSubmitting = true;
    this.state.lastSubmitTime = currentTime;
    this.setSubmissionState(true);
    
    this.handleFormSubmit().finally(() => {
      // Always reset state when done, regardless of success or failure
      this.state.isSubmitting = false;
      this.setSubmissionState(false);
    });
  }

  /**
   * Handle file input change
   * @param {Event} event - Change event
   * @private
   */
  handleFileChange(event) {
    this.updateFileDisplay(event.target);
  }

  /**
   * Update file display when files are selected
   * @param {HTMLInputElement} fileInput - File input element
   * @private
   */
  updateFileDisplay(fileInput) {
    const fileButton = document.querySelector(WarrantyClaimHandler.CONSTANTS.SELECTORS.FILE_BUTTON);
    const dropzone = document.querySelector(WarrantyClaimHandler.CONSTANTS.SELECTORS.DROPZONE);
    const fileList = document.querySelector(WarrantyClaimHandler.CONSTANTS.SELECTORS.FILE_LIST);
    
    if (fileInput.files && fileInput.files.length > 0) {
      const fileCount = fileInput.files.length;
      const firstName = fileInput.files[0].name;
      
      // Update button text
      const buttonText = fileCount === 1 ? `✓ ${firstName}` : `✓ ${fileCount} files selected`;
      fileButton.textContent = buttonText;
      
      // Update button and dropzone styling
      this.applySuccessStyles(fileButton);
      
      if (dropzone) {
        this.applySuccessStyles(dropzone, true);
      }
      
      // Show detailed file list with remove buttons
      if (fileList) {
        let fileListHTML = '';
        for (let i = 0; i < fileInput.files.length; i++) {
          const file = fileInput.files[i];
          const fileSize = this.formatFileSize(file.size);
          fileListHTML += `
            <div class="file-item" data-file-index="${i}">
              <div style="display: flex; justify-content: space-between; align-items: center;">
                <span><strong>${file.name}</strong> (${fileSize})</span>
                <button type="button" class="remove-file-btn" data-file-index="${i}" style="background: #dc3545; color: white; border: none; border-radius: 3px; padding: 2px 8px; font-size: 11px; cursor: pointer;">Remove</button>
              </div>
            </div>
          `;
        }
        fileList.innerHTML = fileListHTML;
        fileList.style.display = 'block';
        
        // Add event listeners to remove buttons
        this.attachRemoveFileListeners();
      }
      
    } else {
      // Reset to default state
      this.resetFileDisplayStyles(fileButton, dropzone, fileList);
    }
  }

  /**
   * Apply success styles to file elements
   * @param {HTMLElement} element - Element to style
   * @param {boolean} isDropzone - Whether element is a dropzone
   * @private
   */
  applySuccessStyles(element, isDropzone = false) {
    const { SUCCESS_COLOR, SUCCESS_BG } = WarrantyClaimHandler.CONSTANTS.STYLES;
    
    element.style.color = SUCCESS_COLOR;
    element.style.backgroundColor = isDropzone ? '#f8f9fa' : SUCCESS_BG;
    element.classList.add('has-files');
    
    if (isDropzone) {
      element.style.borderColor = SUCCESS_COLOR;
    }
  }

  /**
   * Reset file display styles to default state
   * @param {HTMLElement} fileButton - File button element
   * @param {HTMLElement} dropzone - Dropzone element
   * @param {HTMLElement} fileList - File list element
   * @private
   */
  resetFileDisplayStyles(fileButton, dropzone, fileList) {
    const { DEFAULT_COLOR, DEFAULT_BG, BORDER_COLOR } = WarrantyClaimHandler.CONSTANTS.STYLES;
    
    fileButton.textContent = 'Add file';
    fileButton.style.color = DEFAULT_COLOR;
    fileButton.style.backgroundColor = DEFAULT_BG;
    fileButton.classList.remove('has-files');
    
    if (dropzone) {
      dropzone.style.borderColor = BORDER_COLOR;
      dropzone.style.backgroundColor = 'transparent';
      dropzone.classList.remove('has-files');
    }
    
    if (fileList) {
      fileList.style.display = 'none';
      fileList.innerHTML = '';
    }
  }

  /**
   * Format file size in human-readable format
   * @param {number} bytes - File size in bytes
   * @returns {string} Formatted file size
   * @private
   */
  formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes';
    
    const { FILE_SIZE_UNITS, BYTES_PER_UNIT } = WarrantyClaimHandler.CONSTANTS.VALIDATION;
    const unitIndex = Math.floor(Math.log(bytes) / Math.log(BYTES_PER_UNIT));
    const size = (bytes / Math.pow(BYTES_PER_UNIT, unitIndex)).toFixed(2);
    
    return `${parseFloat(size)} ${FILE_SIZE_UNITS[unitIndex]}`;
  }

  /**
   * Attach event listeners to file remove buttons
   * @private
   */
  attachRemoveFileListeners() {
    const removeButtons = document.querySelectorAll(WarrantyClaimHandler.CONSTANTS.SELECTORS.REMOVE_FILE_BTN);
    removeButtons.forEach(button => {
      button.addEventListener('click', this.handleRemoveFile.bind(this));
    });
  }

  /**
   * Handle file removal
   * @param {Event} event - Click event
   * @private
   */
  handleRemoveFile(event) {
    event.preventDefault();
    const fileIndex = parseInt(event.target.getAttribute('data-file-index'));
    this.removeFile(fileIndex);
  }

  /**
   * Remove a file from the file input
   * @param {number} indexToRemove - Index of file to remove
   * @private
   */
  removeFile(indexToRemove) {
    const fileInput = this.elements.fileInput;
    if (!fileInput?.files) {
      return;
    }
    
    try {
      // Create a new FileList without the removed file
      const dataTransfer = new DataTransfer();
      const files = Array.from(fileInput.files);
      
      files.forEach((file, index) => {
        if (index !== indexToRemove) {
          dataTransfer.items.add(file);
        }
      });
      
      // Update the file input with the new FileList
      fileInput.files = dataTransfer.files;
      
      // Update the display
      this.updateFileDisplay(fileInput);
      
    } catch (error) {
      // Silently handle file removal errors
    }
  }

  /**
   * Validate the entire form
   * @returns {Promise<boolean>} True if form is valid
   */
  async validateForm() {
    try {
      this.state.isValidating = true;
      
      // Validate batch code first
      const batchCodeValid = await this.validateBatchCode();
      if (!batchCodeValid) {
        return false;
      }

      // Validate date fields
      const dateValid = this.validateDateFields();
      if (!dateValid) {
        return false;
      }
      
      return true;
    } catch (error) {
      return false;
    } finally {
      this.state.isValidating = false;
    }
  }

  /**
   * Validate date input fields
   * @returns {boolean} True if date fields are valid
   * @private
   */
  validateDateFields() {
    const form = this.elements.form;
    if (!form) return true;
    
    const monthInput = form.querySelector('input[aria-label="Month"]');
    const dayInput = form.querySelector('input[aria-label="Day"]');
    const yearInput = form.querySelector('input[aria-label="Year"]');
    
    if (!monthInput || !dayInput || !yearInput) {
      return true; // Date fields not found, skip validation
    }
    
    const month = monthInput.value.trim();
    const day = dayInput.value.trim();
    const year = yearInput.value.trim();
    
    // Clear previous validation messages
    this.clearDateValidationMessages(monthInput, dayInput, yearInput);
    
    // Only validate if at least one date field is filled
    if (month || day || year) {
      return this.validatePartialDate(month, day, year, monthInput, dayInput, yearInput);
    }
    
    return true;
  }

  /**
   * Clear custom validation messages from date fields
   * @param {...HTMLInputElement} inputs - Date input elements
   * @private
   */
  clearDateValidationMessages(...inputs) {
    inputs.forEach(input => {
      if (input) {
        input.setCustomValidity('');
      }
    });
  }

  /**
   * Validate partial date entry
   * @param {string} month - Month value
   * @param {string} day - Day value  
   * @param {string} year - Year value
   * @param {HTMLInputElement} monthInput - Month input element
   * @param {HTMLInputElement} dayInput - Day input element
   * @param {HTMLInputElement} yearInput - Year input element
   * @returns {boolean} True if date is valid
   * @private
   */
  validatePartialDate(month, day, year, monthInput, dayInput, yearInput) {
    const { MESSAGES } = WarrantyClaimHandler.CONSTANTS;
    
    // Check if all fields are filled
    if (!month || !day || !year) {
      const message = MESSAGES.ERRORS.DATE_INCOMPLETE;
      if (!month) monthInput.setCustomValidity(message);
      if (!day) dayInput.setCustomValidity(message);
      if (!year) yearInput.setCustomValidity(message);
      return false;
    }
    
    // Validate date values
    const monthNum = parseInt(month, 10);
    const dayNum = parseInt(day, 10);
    const yearNum = parseInt(year, 10);
    
    if (isNaN(monthNum) || monthNum < 1 || monthNum > 12) {
      monthInput.setCustomValidity(MESSAGES.ERRORS.MONTH_INVALID);
      return false;
    }
    
    if (isNaN(dayNum) || dayNum < 1 || dayNum > 31) {
      dayInput.setCustomValidity(MESSAGES.ERRORS.DAY_INVALID);
      return false;
    }
    
    const currentYear = new Date().getFullYear();
    if (isNaN(yearNum) || yearNum < 1900 || yearNum > currentYear) {
      yearInput.setCustomValidity(MESSAGES.ERRORS.YEAR_INVALID);
      return false;
    }
    
    // Additional date validation (check if date actually exists)
    const testDate = new Date(yearNum, monthNum - 1, dayNum);
    if (testDate.getMonth() !== monthNum - 1 || testDate.getDate() !== dayNum) {
      dayInput.setCustomValidity('Please enter a valid date.');
      return false;
    }
    
    return true;
  }

  /**
   * Validate batch code against API
   * @returns {Promise<boolean>} True if batch code is valid
   * @private
   */
  async validateBatchCode() {
    const form = this.elements.form;
    if (!form) {
      return false;
    }
    
    const batchCodeInput = form.querySelector('[data-batch-code-input]');
    const batchCode = batchCodeInput?.value?.trim();
    
    if (!batchCode) {
      this.showErrorNotification(
        'Batch Code Validation',
        WarrantyClaimHandler.CONSTANTS.MESSAGES.ERRORS.BATCH_CODE_EMPTY
      );
      return false;
    }

    // Validate format
    const { BATCH_CODE_PATTERN } = WarrantyClaimHandler.CONSTANTS.VALIDATION;
    if (!BATCH_CODE_PATTERN.test(batchCode)) {
      this.showErrorNotification(
        'Batch Code Validation',
        WarrantyClaimHandler.CONSTANTS.MESSAGES.ERRORS.BATCH_CODE_INVALID
      );
      return false;
    }

    try {
      const response = await this.fetchBatchCodeValidation(batchCode);
      const data = await response.json();

      if (data?.status && data?.data && data?.data?.partsJson != null) {
        this.storeBatchCodeData(data);
        return true;
      } else {
        this.showErrorNotification(
          'Batch Code Validation',
          WarrantyClaimHandler.CONSTANTS.MESSAGES.ERRORS.BATCH_CODE_NOT_FOUND
        );
        return false;
      }

    } catch (error) {
      this.showErrorNotification(
        'Batch Code Validation',
        WarrantyClaimHandler.CONSTANTS.MESSAGES.ERRORS.BATCH_CODE_API_ERROR
      );
      return false;
    }
  }

  /**
   * Fetch batch code validation from API
   * @param {string} batchCode - Batch code to validate
   * @returns {Promise<Response>} API response
   * @private
   */
  async fetchBatchCodeValidation(batchCode) {
    const response = await fetch(WarrantyClaimHandler.CONSTANTS.API.BATCH_VALIDATION_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ batchId: batchCode })
    });

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    return response;
  }

  /**
   * Store batch code data in localStorage
   * @param {Object} data - Batch code validation data
   * @private
   */
  storeBatchCodeData(data) {
    try {
      localStorage.setItem('partData', JSON.stringify(data));
      localStorage.setItem('warranty', 'false'); // Store as string for consistency
    } catch (error) {
      // Silently handle storage errors
    }
  }

  /**
   * Handle form submission
   * @returns {Promise<void>}
   */
  async handleFormSubmit() {
    try {
      // Run custom validation (including batch code validation)
      const validationPassed = await this.validateForm();
      if (!validationPassed) {
        // Let the browser show validation messages for HTML5 validation
        this.elements.form.reportValidity();
        return;
      }
      
      // Check HTML5 form validity
      if (!this.elements.form.checkValidity()) {
        // Let the browser show validation messages
        this.elements.form.reportValidity();
        return;
      }
      
      await this.submitForm();

    } catch (error) {
      // Use a more modern notification instead of alert
      this.showErrorMessage('Error: ' + error.message);
    }
  }

  /**
   * Set form submission state
   * @param {boolean} isSubmitting - Whether form is being submitted
   * @private
   */
  setSubmissionState(isSubmitting) {
    this.state.isSubmitting = isSubmitting;
    const submitButton = this.elements.submitButton;
    
    if (submitButton) {
      if (isSubmitting) {
        submitButton.setAttribute('disabled', true);
        submitButton.classList.add('loading');
      } else {
        submitButton.removeAttribute('disabled');
        submitButton.classList.remove('loading');
      }
    }
  }

  /**
   * Show error notification
   * @param {string} title - Notification title
   * @param {string} message - Error message
   */
  showErrorMessage(message) {
    this.showErrorNotification('Error', message);
  }
  
  /**
   * Show success notification
   * @param {string} message - Success message
   */
  showSuccessMessage(message) {
    this.showSuccessNotification('Success', message);
  }

  /**
   * Show error notification
   * @param {string} title - Notification title
   * @param {string} message - Error message
   * @private
   */
  showErrorNotification(title, message) {
    this.showSnotifyNotification(title, message, 'error');
  }

  /**
   * Show success notification
   * @param {string} title - Notification title
   * @param {string} message - Success message
   * @private
   */
  showSuccessNotification(title, message) {
    this.showSnotifyNotification(title, message, 'success');
  }
  
  /**
   * Show snotify notification
   * @param {string} title - Notification title
   * @param {string} message - Notification message
   * @param {string} type - Notification type (success, error, info, warning)
   * @private
   */
  showSnotifyNotification(title, message, type) {
    try {
      if (window.notificationEle && typeof window.notificationEle.updateNotification === 'function') {
        window.notificationEle.updateNotification(title, message, {
          type: type,
          timeout: 5000
        });
      } else {
        // Fallback silently if notification element not found
      }
    } catch (error) {
      // Silently handle notification errors
    }
  }
  
  /**
   * Create FormData object for file upload
   * @returns {FormData} Form data with file
   * @throws {Error} If form element is not found
   * @private
   */
  createFormData() {
    const form = this.elements.form;
    if (!form) {
      throw new Error(WarrantyClaimHandler.CONSTANTS.MESSAGES.ERRORS.FORM_NOT_FOUND);
    }
    
    const formData = new FormData(form);
    const fileInput = this.elements.fileInput;
    
    if (fileInput?.files?.[0]) {
      formData.append('file_data', fileInput.files[0]);
    }
    
    return formData;
  }
  
  /**
   * Upload file to server
   * @param {FormData} formData - Form data containing file
   * @returns {Promise<Object>} Upload response
   * @throws {Error} If upload fails
   * @private
   */
  async uploadFile(formData) {
    try {
      const response = await fetch(this.apiConfig.uploadUrl, {
        method: 'POST',
        headers: {
          'X-Form-Access-Token': this.apiConfig.accessToken
        },
        body: formData
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`File upload failed: ${response.status} - ${errorText}`);
      }
      
      const responseText = await response.text();
      return JSON.parse(responseText);
    } catch (error) {
      throw new Error(`File upload failed: ${error.message}`);
    }
  }
  
  /**
   * Collect all form data for submission in one place
   * @returns {Object} Collected form data
   * @private
   */
  collectFormData() {
    const form = this.elements.form;
    if (!form) {
      throw new Error(WarrantyClaimHandler.CONSTANTS.MESSAGES.ERRORS.FORM_NOT_FOUND);
    }
    
    const requestData = {};
    
    // 1. Collect Batch Code (combine all parts)
    const batchPart1 = form.querySelector('[name="batch_code_part1"]')?.value?.trim();
    const batchPart2 = form.querySelector('[name="batch_code_part2"]')?.value?.trim();
    const batchPart3 = form.querySelector('[name="batch_code_part3"]')?.value?.trim();
    const batchPart4 = form.querySelector('[name="batch_code_part4"]')?.value?.trim();
    
    if (batchPart1 || batchPart2 || batchPart3 || batchPart4) {
      let batchCode = '';
      if (batchPart1) batchCode += batchPart1;
      if (batchPart2) batchCode += (batchCode ? '/' : '') + batchPart2;
      if (batchPart3) batchCode += (batchCode ? '/' : '') + batchPart3;
      if (batchPart4) batchCode += (batchCode ? '-' : '') + batchPart4;
      
      if (batchCode) {
        requestData['Batch Code'] = batchCode;
      }
    }
    
    // 2. Collect Reason for Replacement
    const reasonSelect = form.querySelector('select[name="reason"]') || form.querySelector('select');
    if (reasonSelect?.value) {
      requestData['Reason for Replacement'] = reasonSelect.value;
    }
    
    // 3. Collect Date of Purchase
    const monthInput = form.querySelector('input[aria-label="Month"]');
    const dayInput = form.querySelector('input[aria-label="Day"]');
    const yearInput = form.querySelector('input[aria-label="Year"]');
    
    if (monthInput?.value && dayInput?.value && yearInput?.value) {
      const month = monthInput.value.trim().padStart(2, '0');
      const day = dayInput.value.trim().padStart(2, '0');
      const year = yearInput.value.trim();
      requestData['Date of purchase'] = `${month}/${day}/${year}`;
    }
    
    // 4. Collect Order Number
    const orderNumberInput = form.querySelector('input[aria-label="Order number"]');
    if (orderNumberInput?.value?.trim()) {
      requestData['Order Number'] = orderNumberInput.value.trim();
    }
    
    // 5. Collect Additional Notes
    const notesTextarea = form.querySelector('textarea[aria-label="Additional notes"]') || form.querySelector('textarea');
    if (notesTextarea?.value?.trim()) {
      requestData['Additional Notes'] = notesTextarea.value.trim();
    }
    
    // 6. Collect any other form fields (excluding batch code parts and file inputs)
    const allInputs = form.querySelectorAll('input, textarea, select');
    const excludeNames = ['batch_code_part1', 'batch_code_part2', 'batch_code_part3', 'batch_code_part4'];
    
    allInputs.forEach(input => {
      // Skip if already collected above or should be excluded
      if (input.type === 'file' || 
          excludeNames.includes(input.name) || 
          input.hasAttribute('aria-label') || 
          input.name === 'reason' ||
          !input.value?.trim()) {
        return;
      }
      
      // Get field name from label or fallback to name/id
      let fieldName = input.name || input.id;
      const label = form.querySelector(`label[for="${input.id}"]`) || input.closest('label');
      if (label) {
        const labelText = label.textContent?.replace(/\s*:\s*$/, '').trim();
        if (labelText) fieldName = labelText;
      }
      
      // Add to request data based on input type
      if (input.type === 'checkbox' || input.type === 'radio') {
        if (input.checked) {
          requestData[fieldName] = input.value.trim();
        }
      } else {
        requestData[fieldName] = input.value.trim();
      }
    });
    
    return requestData;
  }
  
  /**
   * Submit form data to API
   * @param {Object} requestData - Form data to submit
   * @returns {Promise<Object>} API response
   * @throws {Error} If submission fails
   * @private
   */
  async submitFormData(requestData) {
    const jsonData = JSON.stringify({ formResponse: requestData });
    
    try {
      const response = await fetch(this.apiConfig.submitUrl, {
        method: 'POST',
        headers: {
          'X-Form-Access-Token': this.apiConfig.accessToken,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: jsonData
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Form submission failed: ${response.status} - ${errorText}`);
      }
      
      return await response.json();
    } catch (error) {
      throw error;
    }
  }
  
  /**
   * Submit the complete form
   * @returns {Promise<void>}
   * @private
   */
  async submitForm() {
    try {
      this.validateFormElements();
      
      // Upload file first
      const imageResponse = await this.handleFileUpload();
      
      // Collect and prepare form data
      const requestData = this.collectFormData();
      this.addFileDataToRequest(requestData, imageResponse);
      
      // Validate order if provided
      await this.handleOrderValidation(requestData);
      
      // Submit form data
      const result = await this.submitFormData(requestData);
      this.handleSubmissionSuccess(result);
      
    } catch (error) {
      this.handleSubmissionError(error);
    }
  }

  /**
   * Validate required form elements exist
   * @throws {Error} If required elements are missing
   * @private
   */
  validateFormElements() {
    const { container, form } = this.elements;
    
    if (!container) {
      throw new Error(WarrantyClaimHandler.CONSTANTS.MESSAGES.ERRORS.CONTAINER_NOT_FOUND);
    }
    
    if (!form) {
      throw new Error(WarrantyClaimHandler.CONSTANTS.MESSAGES.ERRORS.FORM_NOT_FOUND);
    }
  }

  /**
   * Handle file upload process
   * @returns {Promise<Object>} Upload response
   * @private
   */
  async handleFileUpload() {
    const formData = this.createFormData();
    return await this.uploadFile(formData);
  }

  /**
   * Add file data to request if upload was successful
   * @param {Object} requestData - Request data object
   * @param {Object} imageResponse - Upload response
   * @private
   */
  addFileDataToRequest(requestData, imageResponse) {
    if (!imageResponse.error && imageResponse.response?.url) {
      requestData['Proof of Purchase'] = { 
        type: 'file', 
        url: imageResponse.response.url 
      };
    }
  }

  /**
   * Handle warranty validation with cascading fallback system
   * @param {Object} requestData - Request data object
   * @returns {Promise<void>}
   * @private
   */
  async handleOrderValidation(requestData) {
    const orderNumber = requestData['Order Number'];
    const dateOfPurchase = requestData['Date of purchase'];
    
    // Level 1: Order number validation (if provided)
    if (orderNumber) {
      const orderValidationResult = await this.validateOrderDate(orderNumber);
      
      if (orderValidationResult === true) {
        // Order validation succeeded and is within warranty
        this.setWarrantyStatus(true);
        return;
      }
      // If orderValidationResult is null (API failed) or false (outside warranty), continue to date validation
    }
    
    // Level 2: Date of purchase validation (fallback for all cases above)
    if (dateOfPurchase) {
      const dateValidationResult = this.validateDateOfPurchaseLocal(dateOfPurchase);
      this.setWarrantyStatus(dateValidationResult);
    } else {
      this.setWarrantyStatus(false);
    }
  }

  /**
   * Set warranty status in localStorage
   * @param {boolean} isValid - Whether order is within warranty
   * @private
   */
  setWarrantyStatus(isValid) {
    try {
      localStorage.setItem('warranty', isValid ? 'true' : 'false');
    } catch (error) {
      // Silently handle storage errors
    }
  }

  /**
   * Handle successful form submission
   * @param {Object} result - Submission result
   * @private
   */
  handleSubmissionSuccess(result) {
    this.showSuccessMessage(WarrantyClaimHandler.CONSTANTS.MESSAGES.SUCCESS.FORM_SUBMITTED);
    
    // Reset form
    this.resetForm();
    
    // Redirect after delay
    this.scheduleRedirect('/pages/parts-portal', 2000);
  }

  /**
   * Handle form submission error
   * @param {Error} error - Submission error
   * @private
   */
  handleSubmissionError(error) {
    this.showErrorMessage(`Something went wrong: ${error.message}`);
  }

  /**
   * Reset form to initial state
   * @private
   */
  resetForm() {
    const { form, fileInput } = this.elements;
    
    if (form) {
      form.reset();
    }
    
    if (fileInput) {
      this.updateFileDisplay(fileInput);
    }
  }

  /**
   * Schedule page redirect
   * @param {string} url - URL to redirect to
   * @param {number} delay - Delay in milliseconds
   * @private
   */
  scheduleRedirect(url, delay = 2000) {
    setTimeout(() => {
      window.location.href = url;
    }, delay);
  }

  /**
   * Validate order date against warranty period
   * @param {string} orderNumber - Order number to validate
   * @returns {Promise<boolean|null>} True if within warranty, false if not, null if API failed
   * @private
   */
  async validateOrderDate(orderNumber) {
    try {
      const response = await fetch(WarrantyClaimHandler.CONSTANTS.API.ORDER_VALIDATION_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          orderId: parseInt(orderNumber, 10)
        })
      });

      if (!response.ok) {
        return null; // API failed, try next validation method
      }

      const result = await response.json();
      return this.checkWarrantyPeriod(result);

    } catch (error) {
      return null; // API failed, try next validation method
    }
  }

  /**
   * Check if order is within warranty period
   * @param {Object} result - Order validation result
   * @returns {boolean} True if within warranty period
   * @private
   */
  checkWarrantyPeriod(result) {
    const orderDetails = result?.data?.orderDetails;
    if (!orderDetails?.createdAt) {
      return false;
    }

    const orderDate = new Date(orderDetails.createdAt);
    const currentDate = new Date();
    const warrantyPeriodStart = new Date();
    
    warrantyPeriodStart.setMonth(
      currentDate.getMonth() - WarrantyClaimHandler.CONSTANTS.VALIDATION.WARRANTY_PERIOD_MONTHS
    );

    return orderDate >= warrantyPeriodStart;
  }

  /**
   * Validate date of purchase locally (Level 3 validation - fallback)
   * @param {string} dateOfPurchase - Date in MM/DD/YYYY format
   * @returns {boolean} True if within warranty period
   * @private
   */
  validateDateOfPurchaseLocal(dateOfPurchase) {
    try {
      // Parse the date (MM/DD/YYYY format)
      const [month, day, year] = dateOfPurchase.split('/').map(num => parseInt(num, 10));
      
      if (!month || !day || !year) {
        return false;
      }

      const purchaseDate = new Date(year, month - 1, day); // month is 0-indexed in Date constructor
      const currentDate = new Date();
      const warrantyPeriodStart = new Date();
      
      warrantyPeriodStart.setMonth(
        currentDate.getMonth() - WarrantyClaimHandler.CONSTANTS.VALIDATION.WARRANTY_PERIOD_MONTHS
      );

      const isWithinWarranty = purchaseDate >= warrantyPeriodStart;
      return isWithinWarranty;

    } catch (error) {
      return false;
    }
  }
}

/**
 * Initialize WarrantyClaimHandler when DOM is ready
 */
(() => {
  'use strict';
  
  // Auto-initialize when DOM is ready
  const initializeHandler = () => {
    // Only initialize if we're on a page with warranty forms
    const warrantyForm = document.querySelector('.warranty-form');
    if (warrantyForm) {
      new WarrantyClaimHandler();
    }
  };
  
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeHandler);
  } else {
    initializeHandler();
  }
})();